<template>
  <div class="category-slider">
    <!-- Row: Title | Justification Button | Segment bar -->
    <div class="category-slider-row">
      <!-- Title with Tooltip -->
      <span
        v-tooltip="{ text: titleTooltip, portal: true, theme: titleTooltipTheme }"
        class="category-slider-label"
        :class="{ 'cursor-help': titleTooltip }">
        {{ label }}
      </span>

      <!-- Justification Button with Tooltip (dimension sliders only): a lock until the bar is unlocked -->
      <button
        v-if="justifiable"
        v-tooltip="{ text: justificationTooltip, portal: true, theme: tooltipThemeCentered }"
        type="button"
        @click="openJustification"
        class="justification-button data-v-tooltip"
        :aria-label="justificationTooltip"
        :data-testid="isLocked ? 'justification-locked' : 'justification-edit'">
        <IconLock v-if="isLocked" class="h-3.5 w-3.5" stroke="2" />
        <IconPencil v-else class="h-4 w-4" stroke="1.8" />
      </button>
      <span v-else aria-hidden="true"></span>

      <SegmentBar
        :modelValue="modelValue"
        :values="values"
        :zoneLabels="zoneLabels"
        :label="label"
        :locked="isLocked"
        :lockedTooltip="lockedTooltip"
        :testId="`segment-bar-${categoryKey}`"
        :showZoneLabels="showZoneLabels"
        @update:modelValue="(value) => emit('update:modelValue', value)"
        @locked-click="(requested) => emit('openJustification', requested)" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import { IconLock, IconPencil } from '@tabler/icons-vue'
import { useI18n } from 'vue-i18n'
import type { ComplexitySegmentZone } from '@arcexperts/core-types'
import { useWidgetText } from '@/helpers/useWidgetText'
import { hideTooltip } from '@/helpers/tooltip'
import SegmentBar from './SegmentBar.vue'

interface Props {
  modelValue: number
  values: readonly number[]
  zoneLabels: Record<ComplexitySegmentZone, string>
  label: string
  categoryKey: string
  hasJustification?: boolean
  isLocked?: boolean
  /** False for the overall slider, which has no justification. */
  justifiable?: boolean
  showZoneLabels?: boolean
  role?: string
}

const props = withDefaults(defineProps<Props>(), { justifiable: true, showZoneLabels: true })

const emit = defineEmits<{
  'update:modelValue': [value: number]
  /** `requested` is the segment value the user clicked on a locked bar. */
  openJustification: [requested?: number]
}>()

const { wt } = useWidgetText('widget-texts-project-setup', toRef(props, 'role'))
const { t } = useI18n()

const lockedTooltip = computed(
  () => wt('complexity', 'slider', 'locked') || t('project.setup.sections.complexity.locked_hint')
)

function openJustification(event: MouseEvent) {
  hideTooltip(event.currentTarget)
  emit('openJustification', undefined)
}

const justificationTooltip = computed(() => {
  if (props.isLocked) return t('project.setup.sections.complexity.locked_button')
  return props.hasJustification
    ? wt('complexity', 'slider', 'editJustification')
    : wt('complexity', 'slider', 'addJustification')
})

const titleTooltip = computed(() =>
  props.justifiable ? wt('complexity', 'dimensions', props.categoryKey, 'tooltip') : ''
)

const tooltipThemeCentered = { placement: 'top', width: '14rem', padding: '0.6em 1em', 'text-align': 'center' } as const
const titleTooltipTheme = { placement: 'top', width: '18rem', padding: '0.6em 1em' } as const
</script>

<style scoped>
.category-slider {
  @apply overflow-visible py-2;
}

.category-slider-row {
  display: grid;
  grid-template-columns: var(--complexity-label-width, 7rem) 1.5rem minmax(8rem, 1fr);
  align-items: start;
  column-gap: 0.75rem;
}

.category-slider-label {
  @apply min-w-0 truncate pt-0.5 font-medium text-neutral-800;
}

/* Portal tooltips: no hover raise, which would paint the pencil above the justification modal. */
.category-slider-row > .data-v-tooltip:hover {
  z-index: 0;
}

.justification-button {
  @apply mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-300;
}
</style>
